import { mixpanel } from '@affine/track';
import { useEffect, useRef } from 'react';

import type { AuthJourneyId, AuthJourneyViewState } from './journey-manifest';
import {
  consumeAuthJourneyPresentationMarker,
  readAuthJourneyPresentationMarker,
  saveAuthJourneyPresentationMarker,
} from './journey-presentation-state';
import { isAuthJourneyContinuation } from './journey-router';

export type AuthJourneyTelemetryEvent =
  | 'journey_started'
  | 'stage_viewed'
  | 'journey_completed';

export interface AuthJourneyTelemetryPayload {
  journey_id: AuthJourneyId;
  stage_id?: string;
  outcome?: 'completed' | 'exited' | 'failed';
  elapsed_ms?: number;
}

export function trackAuthJourneyEvent(
  event: AuthJourneyTelemetryEvent,
  payload: AuthJourneyTelemetryPayload
) {
  try {
    mixpanel.track(event, payload);
  } catch {
    // Product telemetry must never interrupt authentication.
  }
}

export function completeAuthJourney(
  journeyId: AuthJourneyId,
  outcome: 'completed' | 'exited' | 'failed' = 'completed',
  now = Date.now()
) {
  const activeMarker = readAuthJourneyPresentationMarker(undefined, now);
  if (
    activeMarker &&
    activeMarker.journeyId !== journeyId &&
    isAuthJourneyContinuation(activeMarker.journeyId, journeyId)
  ) {
    return;
  }
  const marker = consumeAuthJourneyPresentationMarker(undefined, now);
  trackAuthJourneyEvent('journey_completed', {
    journey_id: journeyId,
    outcome,
    ...(marker?.journeyId === journeyId
      ? { elapsed_ms: Math.max(0, now - marker.startedAt) }
      : {}),
  });
}

export function useAuthJourneyTelemetry(viewState: AuthJourneyViewState) {
  const seenStages = useRef(new Set<string>());
  const startedJourneys = useRef(new Set<AuthJourneyId>());

  useEffect(() => {
    const currentStageId = viewState.currentStageId;
    if (!currentStageId) return;
    const now = Date.now();
    const marker = readAuthJourneyPresentationMarker();
    const startedAt =
      marker?.journeyId === viewState.journeyId ? marker.startedAt : now;

    saveAuthJourneyPresentationMarker({
      version: 1,
      journeyId: viewState.journeyId,
      stageId: currentStageId,
      startedAt,
    });

    if (!startedJourneys.current.has(viewState.journeyId)) {
      startedJourneys.current.add(viewState.journeyId);
      trackAuthJourneyEvent('journey_started', {
        journey_id: viewState.journeyId,
      });
    }
    const stageKey = `${viewState.journeyId}:${currentStageId}`;
    if (!seenStages.current.has(stageKey)) {
      seenStages.current.add(stageKey);
      trackAuthJourneyEvent('stage_viewed', {
        journey_id: viewState.journeyId,
        stage_id: currentStageId,
      });
    }
  }, [viewState.currentStageId, viewState.journeyId]);
}
